import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Source } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { SourceForm } from "../SourceForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListSources.scss";

const sourceController = new Source();

export function ListSources({ sourcesActive, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [sources,       setSources]       = useState(null);
  const [sourcesFilter, setSourcesFilter] = useState([]);
  const [search,        setSearch]        = useState("");
  const [showModal,     setShowModal]     = useState(false);
  const [selectedSrc,   setSelectedSrc]   = useState(null);
  const [showConfirm,   setShowConfirm]   = useState(false);
  const [confirmMsg,    setConfirmMsg]    = useState("");
  const [isDelete,      setIsDelete]      = useState(false);
  const [rowMenu,       setRowMenu]       = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".sources-list__row-menu, .sources-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setSources(null);
        const res = await sourceController.getSources(accessToken, sourcesActive);
        setSources(res);
        setSourcesFilter(res);
      } catch {
        setSources([]);
        setSourcesFilter([]);
      }
    })();
  }, [sourcesActive, reload]);

  useEffect(() => {
    if (!sources) return;
    if (!search) { setSourcesFilter(sources); return; }
    const q = search.toLowerCase();
    setSourcesFilter(
      sources.filter((s) =>
        s.code?.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q)
      )
    );
  }, [search, sources]);

  const openCreate = () => { setSelectedSrc(null); setShowModal(true); };
  const openEdit   = (s)  => { setSelectedSrc(s);   setShowModal(true); };

  const openToggle = (s) => {
    setSelectedSrc(s);
    setIsDelete(false);
    setConfirmMsg(`¿${s.active ? t("deactivate") : t("activate")} "${s.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (s) => {
    setSelectedSrc(s);
    setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${s.name}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await sourceController.updateSource(accessToken, selectedSrc._id, { active: !selectedSrc.active });
      toast.success(selectedSrc.active ? t("source_deactivated") : t("source_activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_updating")); }
  };

  const handleDelete = async () => {
    try {
      await sourceController.deleteSource(accessToken, selectedSrc._id);
      toast.success(t("source_deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_deleting")); }
  };

  if (sources === null) return <LoadingSpinner />;

  return (
    <div className="sources-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="sources-list__card">
        <div className="sources-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder={`${t("search")}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-source-btn" title={t("create")} onClick={openCreate}>
            <Icon name="plus" />
          </Button>
        </div>

        {sourcesFilter.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="sources-list__tbl-scroll">
            <Table className="sources-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("code")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("name")}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sourcesFilter.map((s) => (
                  <Table.Row key={s._id}>
                    <Table.Cell data-label={t("code")}>{s.code ? s.code.toUpperCase() : ""}</Table.Cell>
                    <Table.Cell data-label={t("name")}>{s.name}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="sources-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="sources-list__row-menu">
                        <button type="button" className="sources-list__row-menu-btn" onClick={(e) => toggleRowMenu(s._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === s._id && ReactDOM.createPortal(
                        <div
                          className="sources-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(s); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(s); setRowMenu(null); }}>
                            <Icon name={s.active ? "ban" : "check"} />{s.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="sources-list__row-menu-item--danger" onClick={() => { openDelete(s); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selectedSrc ? `${t("edit")}: ${selectedSrc.name}` : t("new_source")}
        size="small"
        className="catalog-modal"
      >
        <SourceForm
          source={selectedSrc}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selectedSrc?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
